<script setup lang="ts">
import { ComputedRef, computed, useTemplateRef } from 'vue'

import { type MenuItem } from 'primevue/menuitem'

import { useOfferActions } from '../../composables/useOfferAction'

import { EmployeeShiftOffer } from '../../../employee_shift/models/employeeShiftOffer'
import { Shift } from '../../../shifts/models/shift'
import { SiteID } from '../../../sites/models/site'

/*
|--------------------------------------------------------------------------
| Offer actions
|--------------------------------------------------------------------------
|
| What can be done with one employee's offer: the main actions as buttons
| and the rest in a menu, grouped and separated by their order. Shared by
| the shift's employees tab and the schedule drawer's "Filled by".
*/

const { siteId, shift, offer } = defineProps<{
  siteId: SiteID
  shift: Shift
  offer: EmployeeShiftOffer
}>()

const { mainActions, otherActions, executeAction, isActionLoading } =
  useOfferActions({
    siteId: () => siteId,
    shift: () => shift,
    employee: () => offer.employee!,
    offer: () => offer,
  })

const otherMenu = useTemplateRef('other-menu')
const toggleOtherMenu = (event: Event) => otherMenu.value?.toggle(event)
const otherMenuItems: ComputedRef<MenuItem[]> = computed(() => {
  const menuItems: MenuItem[] = otherActions.value.reduce(
    (items, action, _index, actions) => {
      const menuItem = {
        label: action.label,
        icon: action.icon,
        command: () => executeAction(action.type),
      }

      const prevItem = items[items.length - 1]
      const prevItemAction =
        prevItem && !prevItem.separator
          ? actions.find(
              (a) => a.label === prevItem?.label || a.group === prevItem?.label,
            )
          : undefined
      if (
        prevItem &&
        !prevItem.separator &&
        prevItemAction &&
        prevItemAction.order !== action.order
      ) {
        items.push({ separator: true })
      }

      if (action.group) {
        if (!items.some((item) => item.label === action.group)) {
          items.push({
            label: action.group,
            items: [menuItem],
          })
        } else {
          const groupItem = items.find((item) => item.label === action.group)
          if (groupItem && groupItem.items) {
            groupItem.items.push(menuItem)
          }
        }
      } else {
        items.push({ ...menuItem })
      }

      return items
    },
    [] as MenuItem[],
  )

  return menuItems
})
</script>

<template>
  <div class="flex gap-x-1 place-self-end">
    <Button
      v-for="action in mainActions"
      :key="action.type"
      v-tooltip.top="action.label"
      :icon="action.icon"
      :severity="action.severity"
      :loading="isActionLoading(action.type)"
      :aria-label="action.label"
      size="small"
      @click="executeAction(action.type)"
    />

    <Button
      icon="pi pi-ellipsis-v"
      outlined
      size="small"
      aria-label="More actions"
      aria-haspopup="true"
      @click="toggleOtherMenu"
    />
    <Menu ref="other-menu" :model="otherMenuItems" :popup="true" />
  </div>
</template>
